import { FunctionComponent } from 'react'
import styled, { css } from 'styled-components'

const Skeleton = styled.div`
  ${({
    theme: {
      spacing: { spacing16 },
    },
  }) => css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: ${spacing16};
  `}
`

const SkeletonGrid = styled.div<{ direction?: 'row' | 'column' }>`
  ${({
    theme: {
      spacing: { spacing8 },
    },
    direction = 'column',
  }) => css`
    display: flex;
    flex-direction: ${direction};
    align-items: flex-start;
    width: 100%;
    gap: ${spacing8};
  `}
`

export const SkeletonLine = styled.div<{ width?: string }>`
  ${({
    theme: {
      color: {
        tertiary: { grey200, grey300 },
      },
    },
    width = '100%',
  }) => css`
    min-width: 16px;
    width: ${width};
    height: 16px;
    border-radius: 16px;
    animation: pulse 1s infinite;

    @keyframes pulse {
      0% {
        background-color: ${grey200};
      }
      50% {
        background-color: ${grey300};
      }
      100% {
        background-color: ${grey200};
      }
    }
  `}
`

export const SkeletonImage = styled.div`
  ${({
    theme: {
      color: {
        tertiary: { grey200, grey300 },
      },
    },
  }) => css`
    width: 100%;
    aspect-ratio: 3/2;
    animation: pulse 1s infinite;

    @keyframes pulse {
      0% {
        background-color: ${grey200};
      }
      50% {
        background-color: ${grey300};
      }
      100% {
        background-color: ${grey200};
      }
    }
  `}
`

const SkeletonBullet = styled(SkeletonLine)`
  ${() => {
    return css`
      flex: 0 0 16px;
      border-radius: 50%;
    `
  }}
`

const SkeletonButton = styled(SkeletonLine)<{ height?: string }>`
  ${({ height = '45px' }) => {
    return css`
      height: ${height};
      border-radius: 2px 16px 16px 16px;
    `
  }}
`

export const SkeletonSeedValues = {
  FreeText: 'f',
  Paragraph: 'p1',
  ParagraphNatural: 'p2',
  BulletPoint: 'b',
  QuestionButton: 'q',
  Image: 'i',
} as const

export type SkeletonSeed =
  (typeof SkeletonSeedValues)[keyof typeof SkeletonSeedValues]

type SkeletonSeedWithModifier = { type: SkeletonSeed; multiplier: number }

const isSkeletonSeedWithModifier = (
  seed: SkeletonSeed | SkeletonSeedWithModifier
): seed is SkeletonSeedWithModifier => {
  return Object.prototype.hasOwnProperty.call(seed, 'multiplier')
}

const generateSkeletonLines = (count: number = 1, uneven: boolean = false) =>
  Array(count)
    .fill(0)
    .map((_, index) => (
      <SkeletonLine
        key={`skeleton-line-${index}`}
        width={index % 2 === 0 && uneven ? '95%' : '100%'} // uneven lines make the skeleton look more natural
      />
    ))

const generateSkeletonButtons = (count: number = 1, uneven: boolean = false) =>
  Array.from({ length: count })
    .fill(0)
    .map((_, index) => (
      <SkeletonButton
        key={`skeleton-button-${index}`}
        height={index % 2 === 0 && uneven ? '45px' : '64px'}
        width={index % 2 === 0 && uneven ? '95%' : '100%'}
      />
    ))

export const SkeletonView: FunctionComponent<{
  seed: SkeletonSeed[] | SkeletonSeedWithModifier[]
}> = ({ seed }) => {
  return (
    <Skeleton>
      {seed.map((seedUnguarded, idx) => {
        let seed: SkeletonSeed
        let count = 4

        if (isSkeletonSeedWithModifier(seedUnguarded)) {
          seed = seedUnguarded.type
          count = seedUnguarded.multiplier
        } else {
          seed = seedUnguarded
        }

        if (seed === SkeletonSeedValues.FreeText) {
          return (
            <SkeletonGrid key={`skeleton-grid-paragraph-${idx}`}>
              <SkeletonLine width="75%" />
              <SkeletonLine width="84%" />
              <SkeletonLine width="90%" />
              <SkeletonLine width="78%" />
            </SkeletonGrid>
          )
        } else if (seed === SkeletonSeedValues.Paragraph) {
          return (
            <SkeletonGrid key={`skeleton-grid-paragraph-${idx}`}>
              {generateSkeletonLines(count)}
            </SkeletonGrid>
          )
        } else if (seed === SkeletonSeedValues.ParagraphNatural) {
          return (
            <SkeletonGrid key={`skeleton-grid-paragraph-uneven-${idx}`}>
              {generateSkeletonLines(count, true)}
            </SkeletonGrid>
          )
        } else if (seed === SkeletonSeedValues.QuestionButton) {
          return (
            <SkeletonGrid key={`skeleton-grid-question-${idx}`}>
              {generateSkeletonButtons(count, true)}
            </SkeletonGrid>
          )
        } else if (seed === SkeletonSeedValues.BulletPoint) {
          return (
            <SkeletonGrid
              direction="row"
              key={`skeleton-grid-bullet-point-${idx}`}>
              <SkeletonBullet />
              <SkeletonGrid>{generateSkeletonLines(count)}</SkeletonGrid>
            </SkeletonGrid>
          )
        } else if (seed === SkeletonSeedValues.Image) {
          return (
            <SkeletonGrid key={`skeleton-grid-image-${idx}`}>
              <SkeletonImage />
            </SkeletonGrid>
          )
        }
      })}
    </Skeleton>
  )
}
